import React, { useCallback, useMemo } from 'react';
import * as Clipboard from 'expo-clipboard';
import { defineMessages, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { ViewProps } from 'react-native';

import { openNativeShare, ProductCardV2, ShareIcon } from '@sg/garnish';
import { useDietaryRestrictions } from '@sg/shared-fe-features-menu';
import { useLineItemModifierIngredients } from '@sg/shared-fe-features-ordering';

import { useFeatureFlag } from '@order/LaunchDarkly';
import {
  useLineItemDescription,
  useLineItemIngredients,
  useLineItemName,
} from '@order/shared-hooks';
import { buildShareLineItemUrl } from '@order/utils';

import { useDietaryPreferencesMachine } from '../../context';
import { type PartialLineItem } from '../../graphql';
import { useReorderState } from '../../screens/ReorderingScreen/hooks';

export const SharedLineItem = (props: SharedLineItemProps) => {
  const {
    addToBagLabel,
    lineItem,
    onAddToBag,
    onFavorite,
    isAddingToBag,
    isAddToBagDisabled,
    productContainerStyle,
  } = props;

  const { id, slug, product, removedIngredients, addedIngredients, favorited = false } = lineItem;

  const { formatMessage } = useIntl();
  const { reordering, lineItemId } = useReorderState();

  // ─── Flags ───────────────────────────────────────────────────────────

  const shouldHidePriceOnFavoritesAndShare = useFeatureFlag(
    'CELS-2569-hide-price-on-favorites-and-share',
  );

  const shouldDisableAddToBag =
    isAddToBagDisabled ?? (reordering ? Number(id) !== Number(lineItemId) : undefined);
  const shouldShowAddToBagLoading =
    isAddingToBag ?? (reordering ? Number(id) === Number(lineItemId) : undefined);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleShare = useCallback(() => {
    if (!slug) return;

    const nativeText = formatMessage(messages.shareFavorite, { product_name: product.name });
    const url = buildShareLineItemUrl(slug);

    void openNativeShare({
      message: nativeText,
      url,
      options: { subject: nativeText },
    });
    void Clipboard.setStringAsync(url);
  }, [formatMessage, product.name, slug]);

  // ─────────────────────────────────────────────────────────────────────

  const productName = useLineItemName(product, lineItem);
  const productCostInDollars = shouldHidePriceOnFavoritesAndShare
    ? undefined
    : (product.cost ?? 0) / 100;

  const lineItemIngredients = useLineItemIngredients({
    product,
    addedIngredients,
    removedIngredients,
  });
  const {
    addedIngredientsText: selectedAddedIngredientsText,
    removedIngredientsText: selectedRemovedIngredientsText,
  } = useLineItemModifierIngredients({
    selectedModifiers: lineItem.selectedModifiers,
    showCosts: false,
    showLabels: !product.isCustom,
    product,
  });
  const lineItemDescription = useLineItemDescription(lineItem);

  const { restrictions } = useDietaryPreferencesMachine({ referrer: 'Share' });
  const allergenProperties = product.allergenProperties;
  const getRestrictionNotice = useDietaryRestrictions({ restrictions });
  const restrictionNotice = getRestrictionNotice({ allergenProperties });

  const description = useMemo(() => {
    const selectedModifierDescription = [
      selectedAddedIngredientsText,
      selectedRemovedIngredientsText,
    ]
      .filter(Boolean)
      .join('\n');

    if (selectedModifierDescription) {
      return selectedModifierDescription;
    }

    const { addedIngredientsText, removedIngredientsText } = lineItemIngredients;

    const hasModifications =
      typeof addedIngredientsText === 'string' || typeof removedIngredientsText === 'string';

    if (!hasModifications) {
      return product?.description;
    }

    return [addedIngredientsText, removedIngredientsText].filter(Boolean).join('\n');
  }, [
    lineItemIngredients,
    product?.description,
    selectedAddedIngredientsText,
    selectedRemovedIngredientsText,
  ]);

  const favToggleAccessibilityLabel = favorited
    ? formatMessage(messages.removeFromFavorites, { product: productName })
    : formatMessage(messages.addToFavorites, { product: productName });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ProductCardV2.Container canHover={false} style={productContainerStyle}>
      {onFavorite ? (
        <ProductCardV2.FavoriteToggle
          isActive={favorited}
          onPress={onFavorite}
          accessibilityLabel={favToggleAccessibilityLabel}
        />
      ) : null}

      {product.asset?.url ? (
        <ProductCardV2.Image
          source={product.asset.url}
          alt={product.name}
          isUnavailable={product.outOfStock}
        />
      ) : null}

      <ProductCardV2.LabelContainer>
        {product.label?.name ? (
          <ProductCardV2.Label>{product.label.name}</ProductCardV2.Label>
        ) : null}
      </ProductCardV2.LabelContainer>

      <ProductCardV2.Heading>{productName}</ProductCardV2.Heading>

      {description ? <ProductCardV2.Description>{description}</ProductCardV2.Description> : null}

      {lineItemDescription ? (
        <ProductCardV2.Description>{lineItemDescription}</ProductCardV2.Description>
      ) : null}

      <ProductCardV2.Footer>
        <ProductCardV2.ProductDetailsContainer>
          {productCostInDollars ? (
            <ProductCardV2.ProductDetail palette="dark-kale">
              <FormattedNumber
                value={productCostInDollars}
                // eslint-disable-next-line react/style-prop-object
                style="currency"
                currency="USD"
              />
            </ProductCardV2.ProductDetail>
          ) : null}

          {product.calories === undefined ? null : (
            <ProductCardV2.ProductDetail>
              <FormattedMessage
                {...messages.caloriesText}
                values={{ calories: product.calories ?? 0 }}
              />
            </ProductCardV2.ProductDetail>
          )}
        </ProductCardV2.ProductDetailsContainer>

        {restrictionNotice ? (
          <ProductCardV2.AllergenNotice>{restrictionNotice}</ProductCardV2.AllergenNotice>
        ) : null}
      </ProductCardV2.Footer>

      <ProductCardV2.ButtonsContainer>
        {onAddToBag ? (
          <ProductCardV2.AddToBagButton
            disabled={shouldDisableAddToBag}
            isLoading={shouldShowAddToBagLoading}
            onPress={onAddToBag}
          >
            {addToBagLabel ?? <FormattedMessage {...messages.addToBag} />}
          </ProductCardV2.AddToBagButton>
        ) : null}

        {slug ? <ShareIcon onShare={handleShare} /> : null}
      </ProductCardV2.ButtonsContainer>
    </ProductCardV2.Container>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  shareFavorite: {
    defaultMessage: 'Check out the {product_name} at sweetgreen!',
    description: 'Shared line item | Share favorite',
  },
  caloriesText: {
    defaultMessage: '{calories} cals',
    description: 'Shared line item | Product card | Calories',
  },
  addToFavorites: {
    defaultMessage: 'Add "{product}" to favorites',
    description: 'Shared line item | Add to favorites',
  },
  removeFromFavorites: {
    defaultMessage: 'Remove "{product}" from favorites',
    description: 'Shared line item | Remove from favorites',
  },
  addToBag: {
    defaultMessage: 'Add to bag',
    description: 'Shared line item | Add to bag',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type SharedLineItemProps = {
  lineItem: SharedLineItemLineItem;
  addToBagLabel?: string;
  productContainerStyle?: ViewProps['style'];
  isAddingToBag?: boolean;
  isAddToBagDisabled?: boolean;
  onAddToBag?: () => void;
  onFavorite?: () => Promise<void>;
};

type SharedLineItemLineItem = Omit<PartialLineItem, 'selectedModifiers'> & {
  selectedModifiers?: Parameters<typeof useLineItemModifierIngredients>[0]['selectedModifiers'];
};
